import { type FolderDialogMode, FolderForm, folderFormId } from '@/components/folders/folder-form';
import type { MatrixFolder } from '@mss_creator/shared';
import { ActionBar, Button, Dialog, DialogClose } from '@pms-digital/design-system';
import { useRef, useState } from 'react';

export type { FolderDialogMode };

export interface FolderDialogProps {
  /** What the dialog does; `null` keeps it closed. */
  mode: FolderDialogMode | null;
  companyId: string;
  companyName: string;
  onClose: () => void;
  onSaved: (folder: MatrixFolder) => void;
}

/**
 * « Nouveau dossier » and « Renommer le dossier » (issue #19, planche 16), in
 * the design system's `Dialog`. The focus lands on « Nom ». The dialog cannot
 * be dismissed while the call runs.
 */
export function FolderDialog({
  mode,
  companyId,
  companyName,
  onClose,
  onSaved,
}: FolderDialogProps) {
  const nameRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState(false);
  const renaming = mode?.type === 'rename';

  function handleOpenChange(open: boolean) {
    if (open || pending) return;
    onClose();
  }

  return (
    <Dialog
      open={mode !== null}
      onOpenChange={handleOpenChange}
      title={renaming ? 'Renommer le dossier' : 'Nouveau dossier'}
      initialFocus={nameRef}
      actions={
        <ActionBar
          secondary={
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={pending}>
                Annuler
              </Button>
            </DialogClose>
          }
          primary={
            <Button
              type="submit"
              form={folderFormId}
              variant="secondary"
              loading={pending}
              loadingLabel={renaming ? 'Enregistrement…' : 'Création…'}
            >
              {renaming ? 'Enregistrer' : 'Créer le dossier'}
            </Button>
          }
        />
      }
    >
      {mode && (
        <FolderForm
          // A new form per opening, so no draft or error survives a close.
          key={mode.type === 'rename' ? mode.folder.id : 'create'}
          mode={mode}
          companyId={companyId}
          companyName={companyName}
          nameRef={nameRef}
          onCancel={onClose}
          onSaved={onSaved}
          onPendingChange={setPending}
        />
      )}
    </Dialog>
  );
}
